import { cn } from '@/lib/utils';

export interface CharacterCounterProps {
  current: number;
  max: number;
  className?: string;
  showProgress?: boolean;
  variant?: 'default' | 'compact';
}

export function CharacterCounter({ 
  current, 
  max, 
  className,
  showProgress = false,
  variant = 'default'
}: CharacterCounterProps) {
  const percentage = (current / max) * 100;
  const isNearLimit = percentage >= 80;
  const isOverLimit = current > max;

  const getColorClasses = () => {
    if (isOverLimit) {
      return 'text-red-600 bg-red-50 border-red-200';
    }
    if (isNearLimit) {
      return 'text-amber-600 bg-amber-50 border-amber-200';
    }
    return 'text-slate-600 bg-slate-50 border-slate-200';
  };

  const getProgressColorClasses = () => {
    if (isOverLimit) {
      return 'bg-red-500';
    }
    if (isNearLimit) {
      return 'bg-amber-500';
    }
    return 'bg-blue-500';
  };

  if (variant === 'compact') {
    return (
      <span className={cn(
        'text-xs font-medium',
        isOverLimit ? 'text-red-600' : isNearLimit ? 'text-amber-600' : 'text-slate-500',
        className
      )}>
        {current}/{max}
      </span>
    );
  }

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className={cn(
        'inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border',
        getColorClasses()
      )}>
        {current}/{max}
      </div>
      {showProgress && (
        <div className="w-full bg-slate-200 rounded-full h-1.5">
          <div 
            className={cn(
              'h-1.5 rounded-full transition-all duration-200',
              getProgressColorClasses()
            )}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}